/**
 * GovernanceGate — recommend → human gate → publish flow, with the gate drawn as
 * a bespoke SVG diamond with approve/reject branches. i18n; role="img" + <title>.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { Sparkles, ShieldCheck, Send, Check, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface GovernanceGateProps {
  namespace?: string;
  className?: string;
}

const STEPS = [
  { key: 's1', icon: Sparkles },
  { key: 's2', icon: ShieldCheck },
  { key: 's3', icon: Send },
] as const;

export function GovernanceGate({ namespace = 'marketing.diagrams.governanceGate', className }: GovernanceGateProps) {
  const t = useTranslations(namespace);
  const titleId = 'governance-gate-title';
  return (
    <MotionReveal direction="up" className="min-w-0">
      <div role="img" aria-label={t('title')} className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <svg width="0" height="0" aria-hidden="true" className="absolute">
          <title id={titleId}>{t('title')}</title>
        </svg>
        <ol className="mt-5 grid gap-4 sm:grid-cols-3">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isGate = step.key === 's2';
            return (
              <li
                key={step.key}
                className={cn(
                  'glass-card relative p-5',
                  isGate
                    ? 'border-2 border-cyan-500/40 shadow-glow-cyan-sm'
                    : 'border-l-2 border-cyan-500/30'
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-xl',
                    isGate ? 'bg-cyan-500/15 text-cyan-200' : 'bg-cyan-500/10 text-cyan-300'
                  )}
                >
                  <Icon size={20} aria-hidden="true" />
                </span>
                <p className="text-display mt-3 text-base font-semibold text-white">
                  {t(`steps.${step.key}.title`)}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-white/70">
                  {t(`steps.${step.key}.body`)}
                </p>
                {isGate ? (
                  <div className="mt-3 flex gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-emerald-300">
                      <Check size={12} aria-hidden="true" /> {t('approve')}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-amber-300">
                      <X size={12} aria-hidden="true" /> {t('reject')}
                    </span>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
    </MotionReveal>
  );
}
